/**
 * A tiny, dependency-free source-map line consumer for the app-logic debugger
 * (ADR-0056 TS-debug). QuickJS runs the transpiled JS; this maps between TS (what the
 * developer sees + sets breakpoints on) and JS (what the engine steps), so the debugger
 * shows the TS source — the same model browser devtools use.
 *
 * We need ONLY line granularity (the debug protocol is line-based), so this parses a v3
 * source map's VLQ `mappings` into two line↔line maps + exposes the inlined TS source.
 * Not a full `source-map` consumer (that pulls a heavy wasm dep) — just what we use.
 */

/** Base64 alphabet for the VLQ digits (source-map spec §Base64 VLQ). */
const _B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const _CHAR_TO_INT: Record<string, number> = {};
for (let i = 0; i < _B64.length; i++) _CHAR_TO_INT[_B64[i]] = i;

/** Decode ONE `,`-separated segment string into its integer fields (deltas). */
function decodeSegment(seg: string): number[] {
  const out: number[] = [];
  let shift = 0;
  let value = 0;
  for (const ch of seg) {
    const digit = _CHAR_TO_INT[ch];
    if (digit === undefined) return out; // malformed → stop (best-effort)
    const cont = digit & 32; // continuation bit
    value += (digit & 31) << shift;
    if (cont) {
      shift += 5;
    } else {
      const negate = value & 1;
      value >>= 1;
      out.push(negate ? -value : value);
      value = 0;
      shift = 0;
    }
  }
  return out;
}

export interface LineMapper {
  /** The TS source the map was built from (`sourcesContent[0]`), or null. */
  sourceText: string | null;
  /** JS (generated) 1-based line → TS (original) 1-based line, or null if unmapped. */
  jsToTs(jsLine: number): number | null;
  /** TS (original) 1-based line → JS (generated) 1-based line, or null if unmapped. */
  tsToJs(tsLine: number): number | null;
  /** Map a set of JS executable lines → the distinct TS lines they originate from. */
  mapExecutableLines(jsLines: number[]): number[];
}

interface RawSourceMap {
  mappings?: string;
  sourcesContent?: (string | null)[];
}

/**
 * Build a line mapper from a v3 source-map JSON string. Returns null when the map is
 * absent/unparseable/empty — the caller then falls back to debugging the JS directly.
 */
export function buildLineMapper(mapJson: string | null | undefined): LineMapper | null {
  if (!mapJson) return null;
  let map: RawSourceMap;
  try {
    map = JSON.parse(mapJson) as RawSourceMap;
  } catch {
    return null;
  }
  if (!map.mappings) return null;

  const sourceText = map.sourcesContent?.[0] ?? null;

  // gen(1-based) → min orig(1-based); orig(1-based) → min gen(1-based).
  const jsToTsMap = new Map<number, number>();
  const tsToJsMap = new Map<number, number>();

  // The originalLine delta (field [2]) is CUMULATIVE across the WHOLE mappings string
  // (not reset per generated line) — only generatedColumn resets per line.
  let srcLine = 0; // 0-based running original line
  const lines = map.mappings.split(";");
  for (let g = 0; g < lines.length; g++) {
    const genLine1 = g + 1;
    if (!lines[g]) continue;
    for (const seg of lines[g].split(",")) {
      if (!seg) continue;
      const fields = decodeSegment(seg);
      if (fields.length < 3) continue; // no source position on this segment
      srcLine += fields[2];
      const origLine1 = srcLine + 1;
      // statement start = the smallest orig line mapped to this gen line
      const prevTs = jsToTsMap.get(genLine1);
      if (prevTs === undefined || origLine1 < prevTs) jsToTsMap.set(genLine1, origLine1);
      // and the smallest gen line an orig line maps to (where a breakpoint should bind)
      const prevJs = tsToJsMap.get(origLine1);
      if (prevJs === undefined || genLine1 < prevJs) tsToJsMap.set(origLine1, genLine1);
    }
  }

  return {
    sourceText,
    jsToTs: (jsLine) => jsToTsMap.get(jsLine) ?? null,
    tsToJs: (tsLine) => tsToJsMap.get(tsLine) ?? null,
    mapExecutableLines(jsLines) {
      const out = new Set<number>();
      for (const jl of jsLines) {
        const ts = jsToTsMap.get(jl);
        if (ts != null) out.add(ts);
      }
      return [...out].sort((a, b) => a - b);
    },
  };
}
